// src/lib/types.ts

// API/Database Types
export interface CategoryFromAPI {
  id: string; // uuid
  name: string; // text
  description: string | null; // text
  image_url: string | null; // text
  status: 'active' | 'inactive'; // Assuming status exists based on category.ts
  deleted: boolean; // Assuming deleted exists
  created_at: string; // timestamp with time zone
  updated_at: string; // timestamp with time zone
  // Nested subcategories if fetched together
  subcategories?: SubcategoryFromAPI[];
}

// Re-corrected SubcategoryFromAPI based on category.ts and lib/categories.ts
export interface SubcategoryFromAPI {
  id: string; // uuid
  name: string; // text
  description: string | null; // text
  category_id: string; // uuid
  status: 'active' | 'inactive'; // text or enum
  deleted: boolean; // boolean
  display_order: number; // integer
  created_at: string; // timestamp with time zone
  updated_at: string; // timestamp with time zone
}

export interface ProductCategory { // Helper type for joined data
  id: string;
  name: string;
}

export interface ProductSubcategory { // Helper type for joined data
  id: string;
  name: string;
}

export interface ProductFromAPI {
  id: string; // uuid
  name: string; // text
  description: string; // text
  full_description?: string | null; // text
  price: number; // numeric
  stock: number; // integer
  status: boolean; // boolean
  is_featured: boolean; // boolean
  is_new: boolean; // boolean
  discount?: number | null; // numeric
  image_url?: string | null; // text
  category_id?: string | null; // uuid
  subcategory_id?: string | null; // uuid (Added by migration)
  category?: ProductCategory | null; // Joined relation
  subcategory?: ProductSubcategory | null; // Joined relation
  created_at: string; // timestamp with time zone
  updated_at: string; // timestamp with time zone
  variants?: ProductVariantFromAPI[]; // Assuming a variants table
  images?: ProductImageFromAPI[]; // Assuming a product_images table
}

export interface ProductVariantFromAPI {
  id: string;
  name: string;
  price: number;
  stock: number;
  product_id: string;
}

export interface ProductImageFromAPI {
  id: string;
  image_url: string;
  product_id: string;
  sort_order?: number;
}

// Frontend Types

// Re-corrected Subcategory frontend type based on category.ts
export interface Subcategory {
  id: string;
  name: string;
  description?: string;
  categoryId: string; // Foreign key to parent category
  status: 'active' | 'inactive';
  displayOrder: number;
  // 'deleted', 'createdAt', 'updatedAt' are usually handled internally or not needed directly in frontend components often
}

export interface Category {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  status: 'active' | 'inactive'; // Added based on category.ts
  subcategories?: Subcategory[]; // Nested subcategories
}

// Renamed to Category
export type CategoryInfo = Category;

export interface Product {
  id: string;
  name: string;
  description: string;
  fullDescription?: string;
  price: number;
  stock: number;
  status: boolean;
  isFeatured: boolean;
  isNew: boolean;
  discount?: number;
  imageUrl?: string;
  categoryId?: string;
  subcategory_id?: string; // Added
  category?: ProductCategory; // Keep joined data if useful
  subcategory?: ProductSubcategory; // Added
  variants?: ProductVariant[];
  additionalImages?: string[];
}

export interface ProductVariant {
  id: string;
  name: string;
  price: number;
  stock: number;
  productId: string;
}

// Cart Types
export interface CartItem {
  product: Product;
  quantity: number;
  variant?: ProductVariant;
}

// Order Types
export type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';

// Customer and Payment Types
export interface CustomerInfo {
  name: string;
  phone: string;
  cpf?: string;
  note?: string;
  email?: string;
  address?: string;
  pixKey?: string; // For PIX payment in checkout
  paymentInfo?: PaymentInfo; // Consolidating payment details
}

export interface PaymentInfo {
  method: 'pix' | 'cash' | 'credit_card' | 'credit_card_machine' | 'debit_card_machine';
  receiptUrl?: string; // For PIX proof
  changeFor?: string; // For cash payment
  needsChange?: boolean; // For cash payment
}

// Mapping functions

// Re-corrected mapping function for Subcategory based on category.ts
export function mapSubcategoryFromAPI(sub: SubcategoryFromAPI): Subcategory {
  return {
    id: sub.id,
    name: sub.name,
    description: sub.description || undefined,
    categoryId: sub.category_id,
    status: sub.status,
    displayOrder: sub.display_order,
    // We don't map 'deleted', 'created_at', 'updated_at' to the main frontend type here
  };
}

// Update mapCategoryFromAPI to handle potential nested subcategories and status
export function mapCategoryFromAPI(category: CategoryFromAPI): Category {
  return {
    id: category.id,
    name: category.name,
    description: category.description || undefined,
    imageUrl: category.image_url || undefined,
    status: category.status, // Added status
    // Map nested subcategories if they are included in the API response
    subcategories: category.subcategories?.map(mapSubcategoryFromAPI) || [],
  };
}

// Update mapProductFromAPI to include subcategory_id and subcategory name
export function mapProductFromAPI(product: ProductFromAPI): Product {
  const variants = product.variants?.map(variant => ({
      id: variant.id,
      name: variant.name,
      price: variant.price,
      stock: variant.stock,
      productId: variant.product_id
    }));

  const additionalImages = product.images?.map(image => image.image_url);

  return {
    id: product.id,
    name: product.name,
    description: product.description,
    fullDescription: product.full_description || undefined,
    price: product.price,
    stock: product.stock,
    status: product.status,
    isFeatured: product.is_featured,
    isNew: product.is_new,
    discount: product.discount || undefined,
    imageUrl: product.image_url || undefined,
    categoryId: product.category_id || undefined,
    subcategory_id: product.subcategory_id || undefined, // Mapped
    category: product.category || undefined, // Mapped joined data
    subcategory: product.subcategory || undefined, // Mapped joined data
    variants: variants,
    additionalImages: additionalImages
  };
}
